import { useQuery } from '@apollo/client';
import { Cloud } from '@mui/icons-material';
import { Box, Chip, Grid, MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useUpdateDataSource } from '~/api/data-sources';
import { SettingsBody, SettingsData, SettingsHead } from '~/components/settings';
import { StripedCard } from '~/components/striped-card';
import { useAppContext } from '~/contexts';
import { DataSourceKind } from '~/generated/graphql';
import csv from '~/images/csv.svg';
import xlsx from '~/images/xlsx.svg';
import { formatDate } from '~/lib/datetime';
import { DataSourceDetailsDocument } from '../queries/details.generated';
import { Layout } from './components';

export const DataSourceDetails = () => {
  const { t } = useTranslation();
  const [updateDataSource] = useUpdateDataSource();
  const params = useParams<{ dataSourceId: string }>();
  const dataSourceId = parseInt(params.dataSourceId ?? '');

  const { currentNetwork } = useAppContext();

  const { data } = useQuery(DataSourceDetailsDocument, {
    variables: {
      dataSourceId,
      networkId: currentNetwork.id,
    },
  });

  const dataSource = data?.network?.dataSource;

  return (
    <Layout>
      {dataSource && (
        <>
          <Box>
            <SettingsHead>{t('common.labels.details')}</SettingsHead>
            <SettingsBody>
              <SettingsData className="last">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <StripedCard
                      label={t('common.labels.source')}
                      content={
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {dataSource.kind === DataSourceKind.Integration ? (
                            <Cloud color="primary" fontSize="small" />
                          ) : (
                            <img
                              alt=""
                              src={dataSource.kind === DataSourceKind.Csv ? csv : xlsx}
                              style={{ width: 20, height: 20 }}
                            />
                          )}
                          {dataSource.kind}
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <StripedCard
                      label={t('pages.data_sources.details.created_at')}
                      content={formatDate(dataSource.createdAt)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <StripedCard
                      label={t('pages.data_sources.details.created_by')}
                      content={dataSource.creator?.name ?? '-'}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <StripedCard
                      label={t('pages.data_sources.edit.last_updated_label')}
                      content={formatDate(dataSource.updatedAt)}
                    />
                  </Grid>
                </Grid>
              </SettingsData>
            </SettingsBody>
          </Box>

          <Box sx={{ mt: 3 }}>
            <SettingsHead>{t('pages.data_sources.details.schema')}</SettingsHead>
            <SettingsBody>
              <SettingsData className="last">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      label={t('pages.data_sources.edit.rows_label')}
                      content={dataSource.itemCount.toLocaleString()}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      label={t('pages.data_sources.edit.columns_label')}
                      content={dataSource.keys.length}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      label={t('pages.data_sources.edit.unique_id_label')}
                      content={
                        dataSource.foreignIdName || t('pages.data_sources.edit.unique_id_empty')
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      label={t('pages.data_sources.edit.device_key_label')}
                      content={
                        <Select
                          displayEmpty
                          fullWidth
                          onChange={(event) =>
                            void updateDataSource({
                              variables: {
                                deviceKey: event.target.value,
                                id: dataSource.id,
                                name: dataSource.name,
                              },
                            })
                          }
                          size="small"
                          value={dataSource.deviceKey ?? ''}
                        >
                          <MenuItem value="">
                            {t('pages.data_sources.edit.device_key_empty')}
                          </MenuItem>
                          {dataSource.keys.map((key) => (
                            <MenuItem key={key} value={key}>
                              {key}
                            </MenuItem>
                          ))}
                        </Select>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <StripedCard
                      label={t('pages.data_sources.details.column_names')}
                      content={
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {dataSource.keys.map((key) => (
                            <Chip key={key} label={key} color="primary" size="small" />
                          ))}
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </SettingsData>
            </SettingsBody>
          </Box>
        </>
      )}
    </Layout>
  );
};
